import React from 'react';
import { Link } from 'react-router-dom';

const programHighlights = [
  {
    icon: '📝',
    title: 'Weekly Mock Tests',
    description: 'Rigorous weekly olympiad-style mock tests focused on P1/P2 IMO-level problems, designed to help students advance from Honorable Mention to Medalist.',
  },
  {
    icon: '👨‍🏫',
    title: 'Timely Grading & Feedback',
    description: 'Receive timely, detailed grading and personalized feedback to help you improve your problem-solving.',
  },
  {
    icon: '📊',
    title: 'Progress Tracking',
    description: 'Track your scores, identify weak areas, and monitor improvement over time with our dashboard.',
  },
  {
    icon: '🏆',
    title: 'Competition Preparation',
    description: 'Our ultimate goal is to make you an IMO medalist!',
  },
];

const LandingPage = () => {
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroOverlay}>
          <div style={styles.heroContent}>
            <img 
              src="/imo_sprint_web_icon.png" 
              alt="IMO Sprint Program" 
              style={styles.heroLogo}
            />
            <h1 style={styles.heroTitle}>IMO Sprint Program</h1>
            <p style={styles.heroSubtitle}>
              Elite math olympiad training for ambitious students aiming for national and international competitions
            </p>
            <div style={styles.heroStats}>
              <div style={styles.statItem}>
                <span style={styles.statNumber}>10+</span>
                <span style={styles.statLabel}>IMO Participants</span>
              </div>
              <div style={styles.statDivider} />
              <div style={styles.statItem}>
                <span style={styles.statNumber}>Weekly</span>
                <span style={styles.statLabel}>Mock Contests</span>
              </div>
            </div>
            <div style={styles.heroCta}>
              <Link to="/login" className="btn btn-primary" style={styles.ctaButton}>
                Student Login
              </Link>
              <Link to="/register" className="btn btn-outline" style={styles.ctaButtonOutline}>
                Join the Program
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Program Highlights */}
      <section style={styles.section}>
        <div style={styles.sectionInner}>
          <h2 style={styles.sectionTitle}>What We Offer</h2>
          <p style={styles.sectionSubtitle}>
            A comprehensive training platform designed to push talented students to their mathematical potential
          </p>
          <div style={styles.highlightsGrid}>
            {programHighlights.map((item, index) => (
              <div key={index} style={styles.highlightCard}>
                <div style={styles.highlightIcon}>{item.icon}</div>
                <h3 style={styles.highlightTitle}>{item.title}</h3>
                <p style={styles.highlightDesc}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section style={styles.section}>
        <div style={styles.sectionInner}>
          <h2 style={styles.sectionTitle}>How It Works</h2>
          <div style={styles.stepsGrid}>
            <div style={styles.stepCard}>
              <div style={styles.stepNumber}>1</div>
              <h3 style={styles.stepTitle}>Register & Enroll</h3>
              <p style={styles.stepDesc}>Create your account and get access to the training platform.</p>
            </div>
            <div style={styles.stepArrow}>→</div>
            <div style={styles.stepCard}>
              <div style={styles.stepNumber}>2</div>
              <h3 style={styles.stepTitle}>Solve Problems</h3>
              <p style={styles.stepDesc}>Tackle weekly mock tests with carefully curated olympiad problems.</p>
            </div>
            <div style={styles.stepArrow}>→</div>
            <div style={styles.stepCard}>
              <div style={styles.stepNumber}>3</div>
              <h3 style={styles.stepTitle}>Get Feedback</h3>
              <p style={styles.stepDesc}>Receive detailed grading and personalized feedback from expert coaches.</p>
            </div>
            <div style={styles.stepArrow}>→</div>
            <div style={styles.stepCard}>
              <div style={styles.stepNumber}>4</div>
              <h3 style={styles.stepTitle}>Level Up</h3>
              <p style={styles.stepDesc}>Track your progress and compete at national and international level.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section style={styles.ctaSection}>
        <div style={styles.sectionInner}>
          <h2 style={{ ...styles.sectionTitle, color: '#fff' }}>Ready to Begin Your Journey?</h2>
          <p style={{ ...styles.sectionSubtitle, color: 'rgba(255,255,255,0.85)' }}>
            Join IMO Sprint and take your math competition skills to the next level.
          </p>
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <Link to="/register" className="btn" style={styles.ctaWhiteButton}>
              Get Started Today
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.sectionInner}>
          <div style={styles.footerContent}>
            <div>
              <h3 style={styles.footerBrand}>IMO Sprint Program</h3>
              <p style={styles.footerText}>
                Preparing the next generation of math olympiad champions.
              </p>
            </div>
            <div style={styles.footerLinks}>
              <Link to="/login" style={styles.footerLink}>Login</Link>
              <Link to="/register" style={styles.footerLink}>Register</Link>
              <a href="https://www.imo-official.org/" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>
                IMO Official
              </a>
              <a href="https://artofproblemsolving.com/" target="_blank" rel="noopener noreferrer" style={styles.footerLink}>
                Art of Problem Solving
              </a>
            </div>
          </div>
          <div style={styles.footerBottom}>
            <p style={styles.footerCopyright}>
              © {new Date().getFullYear()} IMO Sprint Program. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const styles = {
  hero: {
    background: 'linear-gradient(135deg, #1a237e 0%, #283593 30%, #1565c0 70%, #0d47a1 100%)',
    minHeight: '500px',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  heroOverlay: {
    width: '100%',
    padding: '80px 20px',
    background: 'radial-gradient(ellipse at top right, rgba(255,255,255,0.08) 0%, transparent 60%)',
  },
  heroContent: {
    maxWidth: '900px',
    margin: '0 auto',
    textAlign: 'center',
    color: 'white',
  },
  heroLogo: {
    width: '150px',
    height: '150px',
    borderRadius: '50%',
    objectFit: 'contain',
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: 'rgba(255,255,255,1)',
    border: '4px solid rgba(255,255,255,0.3)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  },
  heroTitle: {
    fontSize: '56px',
    fontWeight: '800',
    margin: '0 0 16px 0',
    letterSpacing: '-1px',
    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  },
  heroSubtitle: {
    fontSize: '20px',
    fontWeight: '400',
    margin: '0 0 40px 0',
    opacity: 0.9,
    lineHeight: 1.6,
    maxWidth: '650px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  heroStats: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '32px',
    marginBottom: '40px',
    flexWrap: 'wrap',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  statNumber: {
    fontSize: '36px',
    fontWeight: '800',
    color: '#ffd54f',
  },
  statLabel: {
    fontSize: '14px',
    opacity: 0.8,
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  statDivider: {
    width: '1px',
    height: '40px',
    backgroundColor: 'rgba(255,255,255,0.3)',
  },
  heroCta: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  ctaButton: {
    padding: '14px 36px',
    fontSize: '17px',
    fontWeight: '600',
    borderRadius: '8px',
    backgroundColor: '#ffd54f',
    color: '#1a237e',
    border: 'none',
  },
  ctaButtonOutline: {
    padding: '14px 36px',
    fontSize: '17px',
    fontWeight: '600',
    borderRadius: '8px',
    border: '2px solid rgba(255,255,255,0.7)',
    color: '#fff',
    backgroundColor: 'transparent',
  },
  section: {
    padding: '80px 20px',
  },
  sectionInner: {
    maxWidth: '1100px',
    margin: '0 auto',
  },
  sectionTitle: {
    fontSize: '36px',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: '12px',
    color: '#1a237e',
  },
  sectionSubtitle: {
    fontSize: '18px',
    color: '#666',
    textAlign: 'center',
    marginBottom: '48px',
    maxWidth: '600px',
    marginLeft: 'auto',
    marginRight: 'auto',
    lineHeight: 1.6,
  },
  highlightsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '24px',
  },
  highlightCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '32px 24px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  highlightIcon: {
    fontSize: '40px',
    marginBottom: '16px',
  },
  highlightTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#333',
  },
  highlightDesc: {
    fontSize: '14px',
    color: '#666',
    lineHeight: 1.6,
    margin: 0,
  },
  studentsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
  },
  studentCard: {
    backgroundColor: '#f8f9ff',
    borderRadius: '12px',
    padding: '28px 24px',
    textAlign: 'center',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid #e8eaf6',
    transition: 'transform 0.2s ease',
  },
  studentAvatar: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: '#1a237e',
    color: '#ffd54f',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    fontWeight: '700',
    margin: '0 auto 16px',
  },
  studentName: {
    fontSize: '18px',
    fontWeight: '600',
    margin: '0 0 4px 0',
    color: '#333',
  },
  studentCountry: {
    fontSize: '14px',
    color: '#666',
    margin: '0 0 12px 0',
  },
  achievementsList: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '6px',
    marginBottom: '16px',
  },
  achievementBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#e8eaf6',
    color: '#1a237e',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: '500',
  },
  imoLink: {
    display: 'inline-block',
    color: '#1565c0',
    textDecoration: 'none',
    fontWeight: '500',
    fontSize: '14px',
    padding: '6px 12px',
    borderRadius: '6px',
    backgroundColor: '#e3f2fd',
    transition: 'background-color 0.2s ease',
  },
  stepsGrid: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  stepCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '28px 20px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    flex: '1',
    minWidth: '180px',
    maxWidth: '220px',
  },
  stepNumber: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#1a237e',
    color: '#ffd54f',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '700',
    margin: '0 auto 12px',
  },
  stepTitle: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#333',
  },
  stepDesc: {
    fontSize: '13px',
    color: '#666',
    lineHeight: 1.5,
    margin: 0,
  },
  stepArrow: {
    fontSize: '24px',
    color: '#1a237e',
    fontWeight: 'bold',
  },
  ctaSection: {
    padding: '80px 20px',
    background: 'linear-gradient(135deg, #1a237e 0%, #1565c0 100%)',
  },
  ctaWhiteButton: {
    padding: '16px 48px',
    fontSize: '18px',
    fontWeight: '600',
    borderRadius: '8px',
    backgroundColor: '#fff',
    color: '#1a237e',
    border: 'none',
    cursor: 'pointer',
  },
  footer: {
    backgroundColor: '#1a1a2e',
    color: '#fff',
    padding: '48px 20px 24px',
  },
  footerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '32px',
    marginBottom: '32px',
  },
  footerBrand: {
    fontSize: '20px',
    fontWeight: '700',
    margin: '0 0 8px 0',
  },
  footerText: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.6)',
    margin: 0,
  },
  footerLinks: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  },
  footerLink: {
    color: 'rgba(255,255,255,0.7)',
    textDecoration: 'none',
    fontSize: '14px',
    transition: 'color 0.2s ease',
  },
  footerBottom: {
    borderTop: '1px solid rgba(255,255,255,0.1)',
    paddingTop: '24px',
    textAlign: 'center',
  },
  footerCopyright: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.4)',
    margin: 0,
  },
};

export default LandingPage;